import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Alert,
  Fab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Group as GroupIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useClubs, useAuth, useApp } from '../context/AppContext';
import { usePermissions } from '../hooks/usePermissions';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`clubs-tabpanel-${index}`}
      aria-labelledby={`clubs-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ClubCard: React.FC<{
  club: any;
  isMember: boolean;
  currentUser: any;
  activeTab: string;
  onJoinClub?: (clubId: string) => void;
  onViewDetails: (clubId: string) => void;
}> = ({ club, isMember, currentUser, activeTab, onJoinClub, onViewDetails }) => {
  // Use permission hook at the component level (not inside callback)
  const { canManageClubs: hasManageClubsPermission } = usePermissions();

  // Get memberRole from club data (backend provides this)
  const memberRole = club.memberRole;

  // Use canManage flag from backend response (preferred method)
  // Fallback to local calculation if not provided by backend
  const canManageClub = club.canManage !== undefined ? club.canManage : (() => {
    // Fallback calculation (for backwards compatibility)
    // Get facultyMentorId and createdBy from club data
    const facultyMentorId = (club as any)?.facultyMentorId;
    const createdById = club.createdBy;

    // Helper function to normalize ID for comparison
    const normalizeId = (id: any): string | null => {
      if (!id) return null;
      if (typeof id === 'string') return id;
      if (id.$oid) return id.$oid;
      if (id._id) return id._id.toString();
      return id.toString();
    };

    const currentUserId = normalizeId(currentUser?.id);
    const facultyMentorIdNormalized = normalizeId(facultyMentorId);
    const createdByIdNormalized = normalizeId(createdById);

    return (
      (currentUser?.role === 'Faculty' || (currentUser?.role === 'Ambassador' && hasManageClubsPermission)) &&
      (
        (facultyMentorIdNormalized && facultyMentorIdNormalized === currentUserId) ||
        (createdByIdNormalized && createdByIdNormalized === currentUserId) ||
        memberRole === 'President' ||
        memberRole === 'Vice President'
      )
    );
  })();

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'President': return 'error';
      case 'Vice President': return 'warning';
      case 'Secretary': return 'info';
      case 'Treasurer': return 'secondary';
      default: return 'success';
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease-in-out',
      }}
      onClick={() => onViewDetails(club.id)}
    >
      <CardContent sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mr: 2,
              fontSize: '1.5rem',
              bgcolor: 'primary.light',
            }}
          >
            {club.logo || '🏛️'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                {club.name}
              </Typography>
              {activeTab === 'my-clubs' && memberRole && (
                <Chip
                  label={memberRole}
                  size="small"
                  color={getRoleColor(memberRole) as any}
                  sx={{ fontSize: '0.7rem', flexShrink: 0 }}
                />
              )}
              {activeTab === 'explore' && !isMember && (
                <Chip
                  label="Available"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', flexShrink: 0 }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Mentor: {club.facultyMentor || 'N/A'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              <GroupIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {club.memberCount || club.membersCount || (Array.isArray(club.members) ? club.members.length : 0)} member
              {(club.memberCount || club.membersCount || (Array.isArray(club.members) ? club.members.length : 0)) !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            mb: 2,
          }}
        >
          {club.description}
        </Typography>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant={
            activeTab === 'explore'
              ? (club.applicationStatus ? 'outlined' : 'contained')
              : 'outlined'
          }
          color={
            activeTab === 'explore'
              ? club.applicationStatus
                ? club.applicationStatus.status === 'pending' ? 'warning'
                  : club.applicationStatus.status === 'approved' ? 'success'
                  : club.applicationStatus.status === 'rejected' ? 'error'
                  : 'primary'
                : 'success'
              : 'primary'
          }
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click when button is clicked
            if (activeTab === 'explore' && onJoinClub && !club.applicationStatus) {
              onJoinClub(club.id);
            } else {
              onViewDetails(club.id);
            }
          }}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {activeTab === 'explore'
            ? club.applicationStatus
              ? `Application ${club.applicationStatus.status === 'pending' ? 'Pending'
                  : club.applicationStatus.status === 'approved' ? 'Approved'
                  : 'Rejected'} (${club.applicationStatus.percentage}%)`
              : 'Join Club'
            : canManageClub
              ? 'Manage Club'
              : 'View Details'
          }
        </Button>
      </CardActions>
    </Card>
  );
};

const ClubsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = My Clubs, 1 = Explore
  const { clubs, loading, error, refreshClubs } = useClubs();
  const { user: currentUser, isFaculty } = useAuth();
  const { refreshUser } = useApp();
  const { canManageClubs } = usePermissions();
  const navigate = useNavigate();

  // Check if user can create clubs - use permission-based approach
  const canCreateClub = canManageClubs;

  useEffect(() => {
    // Refresh both clubs and user data when page loads or becomes visible
    const refreshData = async () => {
      await Promise.all([
        refreshClubs(),
        refreshUser()
      ]);
    };

    refreshData();

    // Also refresh when window gains focus (user comes back from another tab/app)
    const handleFocus = () => {
      refreshData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount, not when functions change

  // Filter clubs based on active tab and search
  const getFilteredClubs = () => {
    // Ensure clubs is always an array
    const clubsList = Array.isArray(clubs) ? clubs : [];
    let clubsToShow = [];

    if (activeTab === 0) {
      // My Clubs - show clubs where user is a member, creator, or mentor
      clubsToShow = clubsList.filter(club => {
        if (!club) return false;

        const isUserInClubMembers = Array.isArray(club.members) && club.members.some((member: any) => member.user === currentUser?.id || member.id === currentUser?.id);
        const isInUserClubsList = Array.isArray(currentUser?.clubs) && currentUser?.clubs?.some((userClub: any) => {
          // Handle both nested and flat club structures
          const clubId = userClub.club?.id || userClub.id;
          return clubId === club.id;
        });
        const isMentor = currentUser?.role === 'Faculty' && club.facultyMentor === currentUser?.name;

        // Check if user is the creator
        const normalizeId = (id: any): string | null => {
          if (!id) return null;
          if (typeof id === 'string') return id;
          if (id.$oid) return id.$oid;
          if (id._id) return id._id.toString();
          return id.toString();
        };
        const isCreator = normalizeId(club.createdBy) === normalizeId(currentUser?.id);

        return isUserInClubMembers || isInUserClubsList || isMentor || isCreator;
      });
    } else {
      // Explore - show clubs where user is NOT a member, creator, or mentor
      clubsToShow = clubsList.filter(club => {
        if (!club) return false;

        const isUserInClubMembers = Array.isArray(club.members) && club.members.some((member: any) => member.user === currentUser?.id || member.id === currentUser?.id);
        const isInUserClubsList = Array.isArray(currentUser?.clubs) && currentUser?.clubs?.some((userClub: any) => {
          const clubId = userClub.club?.id || userClub.id;
          return clubId === club.id;
        });
        const isMentor = currentUser?.role === 'Faculty' && club.facultyMentor === currentUser?.name;

        // Check if user is the creator
        const normalizeId = (id: any): string | null => {
          if (!id) return null;
          if (typeof id === 'string') return id;
          if (id.$oid) return id.$oid;
          if (id._id) return id._id.toString();
          return id.toString();
        };
        const isCreator = normalizeId(club.createdBy) === normalizeId(currentUser?.id);

        return !isUserInClubMembers && !isInUserClubsList && !isMentor && !isCreator;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      clubsToShow = clubsToShow.filter(club =>
        club && (
          club.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          club.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          club.facultyMentor?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    return clubsToShow;
  };

  const filteredClubs = getFilteredClubs();

  const isUserMember = (club: any) => {
    if (!club) return false;
    const isUserInClubMembers = Array.isArray(club.members) && club.members.some((member: any) => member.user === currentUser?.id || member.id === currentUser?.id);
    const isInUserClubsList = Array.isArray(currentUser?.clubs) && currentUser?.clubs?.some((userClub: any) => {
      const clubId = userClub.club?.id || userClub.id;
      return clubId === club.id;
    });
    return isUserInClubMembers || isInUserClubsList || false;
  };

  const handleJoinClub = (clubId: string) => {
    navigate(`/clubs/${clubId}/quiz`);
  };

  const handleViewDetails = (clubId: string) => {
    navigate(`/clubs/${clubId}`);
  };

  const handleCreateClub = () => {
    navigate('/create-club');
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading clubs..." />;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: { xs: 'white', sm: '#f0f2f5' },
      pb: { xs: 10, sm: 2 }
    }}>
      {/* Mobile Header - Sticky */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#1877f2',
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Clubs
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            textAlign: { xs: 'center', sm: 'left' },
            mt: 0.5
          }}
        >
          {activeTab === 0 ? 'Your clubs and memberships' : 'Discover and join new clubs'}
        </Typography>
      </Box>

      {/* Main Content Container */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 1200 },
        mx: 'auto',
        px: { xs: 0, sm: 2 },
        pt: { xs: 2, sm: 3 }
      }}>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="clubs tabs"
        >
          <Tab
            label={`My Clubs (${Array.isArray(currentUser?.clubs) ? currentUser?.clubs?.length : 0})`}
            id="clubs-tab-0"
            aria-controls="clubs-tabpanel-0"
          />
          <Tab
            label={`Explore (${Array.isArray(clubs) ? clubs.length : 0})`}
            id="clubs-tab-1"
            aria-controls="clubs-tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={`Search ${activeTab === 0 ? 'your clubs' : 'clubs to explore'}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSearchQuery('')}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {searchQuery ? 'Search Results' : 'Your Clubs'} ({filteredClubs.length})
          </Typography>
        </Box>

        {filteredClubs.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
              🏛️
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              No clubs joined yet
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Join clubs from the Explore tab to see them here
            </Typography>
            <Button
              variant="contained"
              onClick={() => setActiveTab(1)}
              sx={{ textTransform: 'none' }}
            >
              Explore Clubs
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {filteredClubs.map((club) => (
              <ClubCard
                key={club.id}
                club={club}
                isMember={isUserMember(club)}
                currentUser={currentUser}
                activeTab="my-clubs"
                onViewDetails={handleViewDetails}
              />
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {searchQuery ? 'Search Results' : 'Explore Clubs'} ({filteredClubs.length})
          </Typography>
        </Box>

        {filteredClubs.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
              🔍
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {searchQuery ? 'No clubs found' : 'No clubs available'}
            </Typography>
            <Typography variant="body1">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'No new clubs are available to join at the moment'}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {filteredClubs.map((club) => (
              <ClubCard
                key={club.id}
                club={club}
                isMember={isUserMember(club)}
                currentUser={currentUser}
                activeTab="explore"
                onJoinClub={handleJoinClub}
                onViewDetails={handleViewDetails}
              />
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Create Club FAB for Faculty and Ambassadors with permission */}
      {canCreateClub && (
        <Fab
          color="success"
          aria-label="create club"
          onClick={handleCreateClub}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            width: 64,
            height: 64,
          }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
        </Fab>
      )}

      </Box>
    </Box>
  );
};

export default ClubsPage;